/** Helpers — tablero información fiscal empresas (despacho). */

import { formatAssignedUsersLabel } from "@/modules/despacho/despachoAssignmentsHelpers";
import { tipoPersonaFiscalLabel } from "@/modules/despacho/despachoFiscalContactHelpers";
import { isObligationRowActive } from "@/modules/despacho/obligationCatalogHelpers";

export const FISCAL_STATUS_BADGE_LABELS = {
  falta_cfs: "Falta CFS",
  sin_obligaciones: "Sin obligaciones",
  revisar_cfs: "Revisar CFS",
  desactualizado: "CFS desactualizado",
  requiere_revision: "Requiere revisión",
  completo: "Completo",
};

export const FISCAL_INFO_FILTER_ALL = "all";
export const FISCAL_INFO_FILTER_SIN_CFS = "sin_cfs";
export const FISCAL_INFO_FILTER_SIN_OBLIGACIONES = "sin_obligaciones";
export const FISCAL_INFO_FILTER_COMPLETAS = "completas";
export const FISCAL_INFO_FILTER_REQUIEREN_REVISION = "requieren_revision";

export const FISCAL_INFO_REVISION_BADGES = ["revisar_cfs", "desactualizado", "requiere_revision"];

export const FISCAL_INFO_SUMMARY_FILTERS = [
  { key: FISCAL_INFO_FILTER_SIN_CFS, label: "Sin CFS", badge: "falta_cfs", tone: "critical" },
  { key: FISCAL_INFO_FILTER_SIN_OBLIGACIONES, label: "Sin obligaciones", badge: "sin_obligaciones", tone: "warn" },
  { key: FISCAL_INFO_FILTER_COMPLETAS, label: "Completas", badge: "completo", tone: "info" },
  {
    key: FISCAL_INFO_FILTER_REQUIEREN_REVISION,
    label: "Requieren revisión",
    badges: FISCAL_INFO_REVISION_BADGES,
    tone: "warn",
  },
];

const BADGE_TONE = {
  falta_cfs: "critical",
  sin_obligaciones: "warn",
  revisar_csf: "warn",
  desactualizado: "warn",
  requiere_revision: "warn",
  completo: "info",
};

/** Debe coincidir con CSF_SYNC_LOGIC_VERSION en backend/modulo_despacho_fiscal_ops.py */
export const CSF_SYNC_LOGIC_VERSION = 2;

export function fiscalStatusBadgeLabel(badge) {
  const key = String(badge || "").trim();
  return FISCAL_STATUS_BADGE_LABELS[key] || key || "—";
}

export function fiscalStatusBadgeTone(badge) {
  return BADGE_TONE[String(badge || "").trim()] || "neutral";
}

function countActiveObligationRows(rows) {
  return (rows || []).filter((row) => isObligationRowActive(row)).length;
}

function csfDetectedNeedsPersist(item) {
  // Solo falta persistir si hay match de catálogo sin fila (active o inactive).
  // Inactive = desactivación manual: no forzar auto-sync.
  return Boolean(item?.obligation_catalog_id) && !item?.assignment;
}

function countPendingCsfMatches(detail) {
  const detected = detail?.obligations?.cfs_detected || [];
  return detected.filter(csfDetectedNeedsPersist).length;
}

/** Indica si el POST sync-csf-internal aplicó obligaciones (aunque suggested_added sea 0). */
export function csfSyncAppliedCount(result) {
  const added = Number(result?.suggested_added || 0);
  const activated = Number(result?.obligations_activated || 0);
  if (added > 0) return added;
  if (activated > 0) return activated;
  return 0;
}

/** ¿Re-sincronizar CFS al abrir detalle? (PDF guardado pero obligaciones no persistidas). */
export function shouldAutoSyncCsfDetail(detail) {
  const meta = detail?.csf_meta || {};
  if (!meta.has_csf) return false;

  const storedVersion = Number(
    detail?.profile?.csf_sync_logic_version
    ?? meta.sync_logic_version
    ?? 0,
  );
  if (storedVersion < CSF_SYNC_LOGIC_VERSION || meta.needs_logic_resync) return true;

  const obls = detail?.obligations || {};
  const activeTotal = countActiveObligationRows([
    ...(obls.cfs || []),
    ...(obls.manual || []),
    ...(obls.cfdi || []),
  ]);
  const pendingMatches = countPendingCsfMatches(detail);
  const pendingSuggestions = (obls.cfs_suggestions || []).length;

  if (pendingMatches > 0 || pendingSuggestions > 0) return true;
  if (!meta.synced_at && activeTotal === 0) return true;

  const activeCfs = countActiveObligationRows(obls.cfs || []);
  if (activeCfs === 0 && pendingMatches === 0 && (obls.cfs_detected || []).length > 0) {
    return !meta.synced_at;
  }
  return false;
}

/** Badge operativo a partir del detalle (prioriza estado real sobre fila del tablero). */
export function resolveFiscalDetailStatusBadge(detail, fallbackBadge = "") {
  if (!detail) return String(fallbackBadge || "").trim();

  const meta = detail.csf_meta || {};
  const obls = detail.obligations || {};
  const activeTotal = countActiveObligationRows([
    ...(obls.cfs || []),
    ...(obls.manual || []),
    ...(obls.cfdi || []),
  ]);
  const pendingMatches = countPendingCsfMatches(detail);
  const pendingSuggestions = (obls.cfs_suggestions || []).length;
  const backendBadge = String(detail.fiscal_status_badge || "").trim();

  if (!meta.has_csf) return "falta_cfs";
  if (meta.needs_logic_resync || Number(meta.sync_logic_version || 0) < CSF_SYNC_LOGIC_VERSION) {
    return "revisar_cfs";
  }
  if (activeTotal <= 0 && (pendingMatches > 0 || pendingSuggestions > 0)) return "revisar_cfs";
  if (activeTotal <= 0) return backendBadge === "revisar_cfs" ? "revisar_cfs" : "sin_obligaciones";
  if (detail.profile?.csf_pending_review || pendingMatches > 0 || pendingSuggestions > 0) {
    return "revisar_cfs";
  }
  if (backendBadge) return backendBadge;
  return activeTotal > 0 ? "completo" : String(fallbackBadge || "sin_obligaciones").trim();
}

/** Aplana grupos del board en lista de ítems. */
export function flattenFiscalInfoGroups(groups) {
  const out = [];
  for (const group of groups || []) {
    for (const item of group?.items || []) {
      out.push({ ...item, regime_group: item.regime_group || group.regime_group });
    }
  }
  return out;
}

/** Resumen numérico para chips de filtro (4 categorías + total). */
export function computeFiscalInfoSummaryCounts(items) {
  const rows = Array.isArray(items) ? items : [];
  return {
    total: rows.length,
    sin_cfs: rows.filter((r) => r.fiscal_status_badge === "falta_cfs").length,
    sin_obligaciones: rows.filter((r) => r.fiscal_status_badge === "sin_obligaciones").length,
    completas: rows.filter((r) => r.fiscal_status_badge === "completo").length,
    requieren_revision: rows.filter((r) =>
      FISCAL_INFO_REVISION_BADGES.includes(r.fiscal_status_badge),
    ).length,
  };
}

/** Deriva conteos desde summary.chips del backend si está disponible. */
export function resolveFiscalInfoSummaryCounts(summary, items) {
  const fromItems = computeFiscalInfoSummaryCounts(items);
  const chips = Array.isArray(summary?.chips) ? summary.chips : [];
  if (!chips.length) {
    return { ...fromItems, total: summary?.total ?? fromItems.total };
  }
  const chipMap = Object.fromEntries(chips.map((c) => [c.badge, c.count]));
  return {
    total: summary?.total ?? fromItems.total,
    sin_cfs: chipMap.falta_cfs ?? fromItems.sin_cfs,
    sin_obligaciones: chipMap.sin_obligaciones ?? fromItems.sin_obligaciones,
    completas: chipMap.completo ?? fromItems.completas,
    requieren_revision:
      (chipMap.revisar_cfs || 0) +
      (chipMap.desactualizado || 0) +
      (chipMap.requiere_revision || 0) ||
      fromItems.requieren_revision,
  };
}

export function itemMatchesFiscalInfoFilter(item, filterKey) {
  const filter = String(filterKey || FISCAL_INFO_FILTER_ALL).trim();
  if (!filter || filter === FISCAL_INFO_FILTER_ALL) return true;
  const badge = String(item?.fiscal_status_badge || "").trim();
  if (filter === FISCAL_INFO_FILTER_SIN_CFS) return badge === "falta_cfs";
  if (filter === FISCAL_INFO_FILTER_SIN_OBLIGACIONES) return badge === "sin_obligaciones";
  if (filter === FISCAL_INFO_FILTER_COMPLETAS) return badge === "completo";
  if (filter === FISCAL_INFO_FILTER_REQUIEREN_REVISION) {
    return FISCAL_INFO_REVISION_BADGES.includes(badge);
  }
  return true;
}

export function filterFiscalInfoItems(items, { searchQuery = "", statusFilter = FISCAL_INFO_FILTER_ALL } = {}) {
  const query = String(searchQuery || "").trim().toLowerCase();
  return (items || []).filter((row) => {
    if (!itemMatchesFiscalInfoFilter(row, statusFilter)) return false;
    if (!query) return true;
    const rfc = String(row.rfc || "").toLowerCase();
    const name = String(row.razon_social || "").toLowerCase();
    const regimen = String(row.regimen_fiscal || "").toLowerCase();
    return rfc.includes(query) || name.includes(query) || regimen.includes(query);
  });
}

/** Reagrupa ítems filtrados por régimen para acordeón. */
export function regroupFiscalInfoItems(items) {
  const groups = new Map();
  for (const item of items || []) {
    const key = item.regime_group || "Sin grupo";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "es"))
    .map(([regime_group, rows]) => ({
      regime_group,
      items: [...rows].sort((a, b) =>
        String(a.razon_social || "").localeCompare(String(b.razon_social || ""), "es"),
      ),
    }));
}

export function mapFiscalInfoTableRow(row) {
  return {
    ...row,
    id: row.fiscal_client_id,
    _tipo: tipoPersonaFiscalLabel(row.tipo_persona),
    _estado: fiscalStatusBadgeLabel(row.fiscal_status_badge),
    _assigned: formatAssignedUsersLabel(row.assigned_users),
  };
}

/** Etiqueta corta de autoridad para chips del catálogo. */
export function obligationAuthorityLabel(authority) {
  const a = String(authority || "").trim();
  return a || "Otra";
}

export const OBLIGATION_AUTHORITY_TONES = {
  SAT: "bg-sky-50 text-sky-900 border-sky-200",
  IMSS: "bg-emerald-50 text-emerald-900 border-emerald-200",
  INFONAVIT: "bg-teal-50 text-teal-900 border-teal-200",
  REPSE: "bg-violet-50 text-violet-900 border-violet-200",
  Estatal: "bg-amber-50 text-amber-950 border-amber-200",
  Interna: "bg-slate-50 text-slate-700 border-slate-200",
};

export function obligationAuthorityChipClass(authority) {
  const key = String(authority || "").trim();
  return OBLIGATION_AUTHORITY_TONES[key] || "bg-slate-50 text-slate-700 border-slate-200";
}

/** Rango ISO de la semana actual (lunes–domingo). */
export function currentWeekRange(referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  const day = ref.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(ref);
  monday.setDate(ref.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(monday), to: fmt(sunday) };
}

/** Rango ISO del mes calendario actual (día 1 — último día). */
export function currentMonthRange(referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const last = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(first), to: fmt(last) };
}

/** 1 de enero del año de referencia (ISO). */
export function currentYearStartIso(referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  return `${ref.getFullYear()}-01-01`;
}

export function isDateInRange(isoDate, from, to) {
  const d = String(isoDate || "").slice(0, 10);
  if (!d) return false;
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

export function filterTasksByWeek(tasks, range = currentWeekRange()) {
  return (tasks || []).filter((t) => isDateInRange(t.due_date, range.from, range.to));
}

export function groupEventsByDate(events) {
  const map = new Map();
  for (const ev of events || []) {
    const date = String(ev.date || "").slice(0, 10) || "Sin fecha";
    if (!map.has(date)) map.set(date, []);
    map.get(date).push(ev);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({ date, items }));
}

export const FISCAL_TASK_STATUS_LABELS = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  completada: "Completada",
  cancelada: "Cancelada",
};

export const FISCAL_TASK_ADVANCE_OPTIONS = [
  { value: "en_proceso", label: "Marcar en proceso" },
  { value: "completada", label: "Marcar completada" },
  { value: "cancelada", label: "Cancelar tarea" },
];
